import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, H1, H2, H3, H4, H5, H6, HR, IconCaret, IconCheck, IconClockFast, P, Small } from '@upsolve/ui';

import * as CONTENT_GROUPS from '../components/analytics/contentGroups';
import Footer from '../components/Layout/Footer';
import Header from '../components/Layout/Header';
import SEO from '../components/SEO/SEO';
import ReviewsWall from '../components/reviews/ReviewsWall';
import TopLevelLayout from '../components/Layout/TopLevelLayout';
import getScreenerUrl from '../components/Links/getScreenerUrl';
import upsolveJSONLD from '../components/SEO/upsolveJSONLD';
import webApplicationJSONLD from '../components/SEO/webApplicationJSONLD';
import trackComponentAction from '../components/analytics/trackComponentAction';
import trackComponentImpression from '../components/analytics/trackComponentImpression';
import trackPageScroll from '../components/analytics/trackPageScroll';
import trackPageView from '../components/analytics/trackPageView';
import trackWindowClosed from '../components/analytics/trackWindowClosed';
import withScrollWatcher from '../components/analytics/withScrollWatcher';
import withViewportWatch from '../components/analytics/withViewportWatch';
import { A } from '../components/analytics/LinksWithOnClick';
import SCREENER_CTA_TYPES from '../components/analytics/screenerCtaTypes';
import PhoneDemo from '../components/homePage/PhoneDemo';
import PhoneDemoChecklistFiling from '../components/homePage/PhoneDemoChecklistFiling';
import PhoneFormsGenerated from '../components/homePage/PhoneFormsGenerated';
import ImpactMap from '../components/homePage/ImpactMap';
import DebtIncrementer from '../components/ProofBuilders/DebtIncrementer';
import Portal from '../components/overlays/Portal';
import VideoModal from '../components/overlays/VideoModal';
import LaptopDemoVideo from '../components/homePage/LaptopDemoVideo';
import HomePageFAQ from '../components/homePage/HomePageFAQ';

const contentGroup = CONTENT_GROUPS.HOME_PAGE;
const pageTemplatePrpos = {
  pageComponentName: 'HomePage',
  pageComponentVersion: 4,
  contentGroup,
  searchTermTarget: 'upsolve',
};

const ForbesLogo = styled.a`background: url("${require('../../static/images/forbes.png')}");`;
const WSJLogo = styled.a`background: url("${require('../../static/images/wsj.png')}");`;
const WPLogo = styled.a`background: url("${require('../../static/images/wp.png')}");`;
const NYTLogo = styled.a`background: url("${require('../../static/images/nyt.png')}");`;
const FCLogo = styled.a`background: url("${require('../../static/images/fc.png')}");`;

/**
 * HomePage
 * - v0: Unknown (I believe matched to HomePageCover)
 * - v1: Unknown (I believe matched to HomePageCover)
 * - v2: Unknown (I believe matched to HomePageCover)
 * - v3: Unknown (I believe matched to HomePageCover)
 * - v4: Animated Phone, Interactive Map, Cooler Looking Reviews. Big impressive change
 *
 * @component
 * @version 4
 */
const HomePage = ({ scrollWatcher }) => {
  // - Page View
  useEffect(() => {
    trackPageView(pageTemplatePrpos);
    const windowClosedTracker = () => trackWindowClosed(pageTemplatePrpos);
    window.addEventListener('beforeunload', windowClosedTracker);
    return () => {
      window.removeEventListener('beforeunload', windowClosedTracker);
    }
  }, []);
  // - Page Scroll
  const [scrollIntervalCache, setScrollIntervalCache] = useState([0]);
  useEffect(() => {
    if (Math.max(...scrollIntervalCache) < scrollWatcher.scrollInterval) {
      setScrollIntervalCache(scrollIntervalCache.concat(scrollWatcher.scrollInterval));
      trackPageScroll(pageTemplatePrpos);
    }
  }, [scrollWatcher.scrollInterval]);
  // Interactive Handlers
  const [watchCreditorVideo, setWatchCreditorVideo] = useState(false);

  return (
    <TopLevelLayout showPersistentCta={false}>
      <div className="index-container">
        <SEO
          title="File Bankruptcy for Free | TurboTax for Bankruptcy | Nonprofit"
          description="Upsolve is America's #1 free tool for bankruptcy. Harvard-funded nonprofit featured 4x in Forbes."
          image="https://static.upsolve.org/app-preview-bubble.png"
          url="https://upsolve.org/"
          jsonLD={[upsolveJSONLD, webApplicationJSONLD]}
        />
        <Header allowMobileFooter={false} />
        <StyledHomePage>
          <section className="hp__hero">
            <div className="hp__hero__cta">
              <div className="hp__hero__cta__copy">
                <header>
                  <h1>America's #1 free bankruptcy tool</h1>
                </header>
                <div>
                  <P>Thousands of Americans use Upsolve’s free nonprofit tool to generate their Chapter 7 bankruptcy forms and relieve their debt. They also receive free education, community, and support. We never ask you for a credit card. Funded by Harvard University and featured 4x in Forbes.</P>
                </div>
                <footer>
                  <A
                    className="start-button"
                    href={getScreenerUrl({ cta: SCREENER_CTA_TYPES.HOME_PAGE_HERO, contentGroup })}
                    onClick={() => {
                      // Legacy tracking component name: 'homePageCover'
                      // - v4: highly detailed animated phone w/ new bold h1
                      trackComponentAction({ componentName: 'homePageCover', componentVersion: 4, actionId: 'homePageCover.screenerNavigation', actionMethod: 'click',  });
                    }}
                  >
                    Start now <IconCaret />
                  </A>
                  <a className="learn-more-button" href="#reviews">
                    Hear from users <IconCaret />
                  </a>
                </footer>
              </div>
              <div className="hp__hero__cta__demo">
                <PhoneDemo />
              </div>
            </div>
            <div className="hp__hero__scene-front" />
            <div className="hp__hero__scene-back" />
          </section>
          <section className="hp__reviews" id="reviews">
            <ReviewsWall />
          </section>
          <section className="hp__value-prop">
            <div className="hp__value-prop__copy">
              <H6 as="label">Free Forever</H6>
              <H1 as="h2">Generate your bankruptcy paperwork for free!</H1>
              <ul>
                <li>
                  <IconCheck /> <P>No paywalls, no payments, no credit cards. We’re a nonprofit funded by generous donors like Harvard University, the former CEO of Google, and the Legal Services Corporation.</P>
                </li>
                <li>
                  <IconCheck /> <P>Access on any mobile or desktop web browser.</P>
                </li>
                <li>
                  <IconCheck /> <P>Designed and built alongside experienced bankruptcy attorneys.</P>
                </li>
              </ul>
              <div>
                <A className="start-button" href={getScreenerUrl({ cta: SCREENER_CTA_TYPES.HOME_PAGE_VALUE_PROPS, contentGroup })}>Take Upsolve Screener <IconCaret /></A>
              </div>
            </div>
            <div className="hp__value-prop__forms">
              <div className="phone">
                <PhoneFormsGenerated />
              </div>
            </div>
          </section>
          <section className="hp__value-prop mobile-reverse">
            <div className="hp__value-prop__devices">
              <div className="back"><LaptopDemoVideo /></div>
              <div className="front"><PhoneDemoChecklistFiling /></div>
            </div>
            <div className="hp__value-prop__copy">
              <H6 as="label">Free Education, Community, and Support</H6>
              <H1 as="h2">Bankruptcy is a big decision. We're here at every step</H1>
              <ul>
                <li>
                  <IconCheck />
                  <P>Receive articles and videos created by experienced bankruptcy attorneys for every step of the bankruptcy process. <u onClick={() => setWatchCreditorVideo(true)}>Watch our "341 meeting" prep video!</u></P>
                </li>
                <li>
                  <IconCheck />
                  <P>Get real time court updates via text message after you file.</P>
                </li>
                <li>
                  <IconCheck />
                  <P>Join the Facebook group of Upsolve users and get customer support.</P>
                </li>
              </ul>
              <div>
                <A className="start-button" href={getScreenerUrl({ cta: SCREENER_CTA_TYPES.HOME_PAGE_VALUE_PROPS, contentGroup })}>Take Upsolve Screener <IconCaret /></A>
              </div>
            </div>
            {watchCreditorVideo && <Portal><VideoModal videoUrl="https://www.youtube.com/watch?v=GLkpEHCwYqI" onClose={() => setWatchCreditorVideo(false)} /></Portal>}
          </section>
          <section className="hp__impact" id="impact">
            <H6 as="label" className="live-updates">
              <svg><circle cx="5" cy="5" r="5" /></svg> The Upsolve System is Online
            </H6>
            <H3 as="p" className="debt-relieved">Total debt relieved by Upsolve users: <span className="incrementer">$<DebtIncrementer /></span></H3>
            <ImpactMap />
          </section>
          <section className="hp__nascar">
            <div>
              <WSJLogo target="_blank" href="https://www.wsj.com/articles/for-struggling-consumers-a-cheaper-way-to-file-for-bankruptcy-1505096071" />
              <NYTLogo target="_blank" href="https://www.nytimes.com/2018/12/21/technology/year-in-technology-2018.html" />
              <ForbesLogo target="_blank" href="https://www.forbes.com/sites/chrisdenhart/2018/09/19/upsolve-moves-upward-under-30-cofounders-bankruptcy-service-is-just-one-click-away/#2d4916916747" />
              <FCLogo target="_blank" href="https://www.fastcompany.com/90457894/not-for-profit-most-innovative-companies-2020" />
              <WPLogo target="_blank" href="https://www.washingtonpost.com/outlook/what-happens-when-you-cant-afford-to-go-bankrupt/2018/03/02/343fd882-1d8e-11e8-9de1-147dd2df3829_story.html" />
            </div>
          </section>
          <section className="hp__faq">
            <div className="hp__faq__header">
              <H4 as="label">Bankruptcy F.A.Q.s</H4>
              <Small>Click a question to open and learn more!</Small>
            </div>
            <HomePageFAQ />
          </section>
        </StyledHomePage>
        <Footer bio={false} />
      </div>
    </TopLevelLayout>
  );
}

const StyledHomePage = styled.main`
  // HERO
  .hp__hero__cta {
    width: 100%;
    max-width: 1080px;
    margin: -54px auto 0;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    padding: 64px 16px 150px;
    min-height: 95vh;
    @media(max-width: ${props => props.theme.breakpoints[500]}) {
      grid-template-columns: 1fr;
      padding: 40px 16px 0;
    }
  }
  .hp__hero__cta__copy, .hp__hero__cta__demo {
    max-width: 100vw;
  }
  .hp__hero__cta__copy {
    display: grid;
    row-gap: 1em;
    header {
      padding-left: 16px;
      color: ${props => props.theme.colors.brand[500]};
      display: flex;
      align-items: flex-end;
      min-height: 200px;
      min-width: calc(100% + 100px);
      h1 {
        font-family: Platform, sans-serif;
        font-size: 80px;
        letter-spacing: 0px;
        line-height: 90%;
        margin-top: 80px;
      }
    }
    & > div, & > footer {
      padding: 0 16px;
    }
    div {
      p {
        font-size: 18px;
        color: ${props => props.theme.colors.gray[700]};
      }
    }
    footer {
      a {
        margin: 0 16px 16px 0;
        font-weight: 500;
        svg {
          height: 12px;
          width: 12px;
          transform: rotate(-90deg);
          opacity: 0.7;
          position: relative;
          top: 1px;
        }
      }
      a.start-button {
        color: white;
        background: ${props => props.theme.colors.brand[500]};
        border-bottom: 2px solid ${props => props.theme.colors.brand[300]};
        padding: 0.5em 1em 0.5em 1.25em;
        border-radius: 7px;
        &:hover {
          background: ${props => props.theme.colors.brand[500]};
        }
        svg path {
          fill: white;
        }
      }
      a.learn-more-button {
        color: ${props => props.theme.colors.gray[500]};
        svg path {
          fill: ${props => props.theme.colors.gray[500]};
        }
      }
    }
    @media(max-width: ${props => props.theme.breakpoints[500]}) {
      header {
        padding-left: 0;
        min-width: 100%;
        max-width: 100%;
        h1 {
          font-size: 54px;
        }
      }
      header, footer, & > div {
        padding: 0;
      }
    }
  }
  .hp__hero__cta__demo {
    @media(max-width: ${props => props.theme.breakpoints[500]}) {
      margin: 4em auto 1em;
      figure {
        margin: 0;
      }
    }
  }
  .hp__hero__scene-front, .hp__hero__scene-back {
    position: absolute;
  }
  .hp__hero__scene-front {
    top: 40px;
    left: 0;
    right: 0;
    height: 100vh;
    z-index: -1;
    background: url("/images/illustrations/home-cover-front.svg");
    background-repeat: no-repeat;
    background-position: bottom;
    background-size: contain;
  }
  .hp__hero__scene-back {
    top: 0;
    left: 0;
    right: 0;
    height: 100vh;
    max-width: 960px;
    margin-left: auto;
    z-index: -2;
    background: url("/images/illustrations/home-cover-back.svg");
    background-repeat: no-repeat;
    background-position: bottom;
    background-size: contain;
    opacity: 0.7;
  }
  .hp__hero__scene-cloud {}

  // REVIEWS
  .hp__reviews {
    margin: 3em 0;
  }

  // MEDIA LOGOS
  .hp__nascar {
    & > div {
      width: 100%;
      max-width: 1080px;
      margin: 0 auto;
      height: auto;
      min-height: 96px;
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      & > a {
        display: flex;
        align-items: center;
        justify-content: center;
        max-height: 54px;
        margin: 0 1em;
        background-position: center;
        background-size: contain;
        background-repeat: no-repeat;
        filter: opacity(0.3) drop-shadow(0 0 0 ${props => props.theme.colors.brand[500]});
        &:hover { cursor: pointer; }
      }
      @media(max-width: ${props => props.theme.breakpoints[500]}) {
        grid-template-columns: 1fr;
        margin: 0 auto 2em;
        & > a {
          padding: 1em;
          margin: 1em;
        }
      }
    }
  }

  // MAP
  .hp__impact {
    width: 100%;
    max-width: 1080px;
    margin: 3em auto 2em;
    overflow: visible;
    text-align: center;
    .live-updates {
      display: flex;
      justify-content: center;
      align-items: center;
      color: ${props => props.theme.colors.brand[700]};
      svg {
        height: 14px;
        width: 14px;
        margin: 4px 2px 0 0;
        circle {
          height: 100%;
          width: 100%;
          @keyframes live-updates-blinker {
            0% { fill: ${props => props.theme.colors.green[500]}; }
            100% { fill: ${props => props.theme.colors.green[100]}; }
          }
          animation: live-updates-blinker 0.75s alternate infinite;
        }
      }
    }
    .debt-relieved {
      color: ${props => props.theme.colors.brand[500]};
      display: flex;
      justify-content: center;
      margin: 0.25em 0 0;
      .incrementer {
        width: 210px;
        overflow: visible;
        text-align: left;
        margin-left: 8px;
        text-decoration: underline;
      }
      @media(max-width: ${props => props.theme.breakpoints[500]}) {
        flex-direction: column;
        justify-content: center;
        align-items: center;
      }
    }
  }

  // VALUE PROPS
  .hp__value-prop {
    width: 100%;
    max-width: 1080px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    height: auto;
    min-height: 85vh;
    @media(max-width: ${props => props.theme.breakpoints[500]}) {
      grid-template-columns: 1fr;
      margin: 3em auto;
    }
    .hp__value-prop__copy {
      display: flex;
      flex-direction: column;
      justify-content: center;
      label {
        color: ${props => props.theme.colors.brand[500]};
        font-weight: 500;
        margin-bottom: 0.75em;
      }
      h2 {
        font-family: Platform, sans-serif;
        margin-right: -1em;
      }
      ul, ol {
        list-style-type: none;
        margin: 0;
        padding: 0 0 2.5em;
      }
      li {
        display: flex;
        padding-top: 12px;
        margin-top: 12px;
        &:not(:first-of-type) {
          border-top: 1px solid ${props => props.theme.colors.white[500]};
        }
        svg, path {
          fill: ${props => props.theme.colors.green[500]};
          max-height: 15px;
          max-width: 15px;
          margin: 4px 12px 4px 0;
        }
        p {
          padding: 0;
          margin: 0;
          font-size: 16px;
          font-weight: 300;
          color: ${props => props.theme.colors.gray[500]};
        }
        u {
          color: ${props => props.theme.colors.brand[500]};
          &:hover {
            cursor: pointer;
          }
        }
      }
      a.start-button {
        color: white;
        background: ${props => props.theme.colors.brand[500]};
        border-bottom: 2px solid ${props => props.theme.colors.brand[300]};
        padding: 0.5em 1em 0.5em 1.25em;
        border-radius: 7px;
        margin: 0 16px 16px 0;
        font-weight: 500;
        svg {
          height: 12px;
          width: 12px;
          transform: rotate(-90deg);
          opacity: 0.7;
          position: relative;
          top: 1px;
        }
        &:hover {
          background: ${props => props.theme.colors.brand[500]};
        }
        svg path {
          fill: white;
        }
      }
      @media(max-width: ${props => props.theme.breakpoints[800]}) {
        padding: 16px;
        h2 {
          margin-right: 0;
        }
        & > div {
          display: flex;
          text-align: center;
          .start-button {
            flex-grow: 1;
          }
        }
      }
    }
    .hp__value-prop__devices {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      figure {
        position: relative;
        margin: 0 auto;
        left: -25%;
      }
      & > div {
        height: 540px;
        margin: auto 0;
        overflow: visible;
        &.front {
          z-index: 5;
        }
        &.back {
          z-index: -5;
          transform: scale(0.75);
          figure {
            left: -55%;
          }
        }
      }
      @media(max-width: ${props => props.theme.breakpoints[700]}) {
        margin: 2em 0;
        grid-template-columns: 1fr;
        figure {
          left: 0;
          margin: 0 auto;
        }
        div.back {
          display: none;
        }
      }
    }
    .hp__value-prop__forms {
      display: flex;
      justify-content: center;
      align-items: center;
      background: url("/images/illustrations/home-cover-back.svg");
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
      @media(max-width: ${props => props.theme.breakpoints[700]}) {
        overflow: hidden;
        padding: 1em 0 5em;
      }
    }
    @media(max-width: ${props => props.theme.breakpoints[700]}) {
      display: flex;
      flex-direction: column;
      min-height: auto;
      &.mobile-reverse {
        flex-flow: column-reverse;
      }
    }
  }

  // FAQ
  .hp__faq {
    background: ${props => props.theme.colors.white[700]};
    &__header {
      text-align: center;
      padding: 3em 2em 0.5em;
      display: flex;
      flex-direction: column;
      align-items: center;
      label {
        color: ${props => props.theme.colors.brand[500]};
      }
      small {
        opacity: 0.5;
        margin-top: 0.5em;
      }
    }
  }
`;

export default withScrollWatcher(HomePage);
